/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable react/no-danger */
import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from '@components/v2/Modal';
import { Button } from '@ui';
import Label from '@components/Label';
import InspectionStatusSelector from '@components/v2/InspectionStatusSelector';
import { translate } from '@utils/i18n';
import { toast } from 'react-hot-toast';
import useBulkCompleteReview from '../hooks/useBulkCompleteReview';
import InspectionsReviewService from '../services/InspectionsReviewService';
const namespace = 'features.ai_to_review_inspections.bulk_actions';
function BulkCompleteReviewModal({ isOpen, inspections, selectAll, totalCount, filters, onClose }) {
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [allInspectionIds, setAllInspectionIds] = useState(null);
    const [allUnresolvedIssues, setAllUnresolvedIssues] = useState(null);
    const bulkCompleteMutation = useBulkCompleteReview();
    const fetchAllInspectionIds = useCallback(() => {
        setAllInspectionIds(null);
        setAllUnresolvedIssues(null);
        setLoadError(false);
        setIsLoadingData(true);
        InspectionsReviewService.getAllInspectionIds(filters)
            .then((response) => {
            setAllInspectionIds(response.inspection_ids);
            setAllUnresolvedIssues(response.total_unresolved_issues);
        })
            .catch((error) => {
            console.error('Error fetching all inspection IDs:', error);
            setAllInspectionIds(null);
            setAllUnresolvedIssues(null);
            setLoadError(true);
            toast.error(translate('load_inspection_ids_error', { namespace }));
        })
            .finally(() => {
            setIsLoadingData(false);
        });
    }, [filters]);
    // Fetch all IDs and unresolved count when modal opens with selectAll
    useEffect(() => {
        if (isOpen) {
            setSelectedStatus(null);
            setLoadError(false);
            if (selectAll) {
                fetchAllInspectionIds();
            }
            else {
                // Reset when not selectAll
                setAllInspectionIds(null);
                setAllUnresolvedIssues(null);
            }
        }
    }, [isOpen, selectAll, fetchAllInspectionIds]);
    const handleStatusSelect = (status) => {
        setSelectedStatus((status === null || status === void 0 ? void 0 : status.value) || null);
    };
    const handleComplete = () => {
        let inspectionIds;
        if (selectAll) {
            if (!allInspectionIds || allInspectionIds.length === 0)
                return;
            inspectionIds = allInspectionIds;
        }
        else {
            if (inspections.length === 0)
                return;
            inspectionIds = inspections.map((inspection) => inspection.id);
        }
        bulkCompleteMutation.mutate({
            inspectionIds,
            statusId: selectedStatus
        }, {
            onSuccess: () => {
                onClose();
            }
        });
    };
    const handleClose = () => {
        if (!bulkCompleteMutation.isLoading && !isLoadingData) {
            setSelectedStatus(null);
            setAllInspectionIds(null);
            setAllUnresolvedIssues(null);
            setLoadError(false);
            onClose();
        }
    };
    const isProcessing = bulkCompleteMutation.isLoading;
    // Use totalCount when selectAll is true, otherwise use inspections array length
    const inspectionCount = selectAll ? totalCount : inspections.length;
    // For unresolved issues: use fetched count when selectAll, otherwise calculate from loaded inspections
    const localUnresolvedIssues = inspections.reduce((sum, inspection) => sum + (inspection.unresolved || 0), 0);
    const totalUnresolvedIssues = selectAll && allUnresolvedIssues !== null ? allUnresolvedIssues : localUnresolvedIssues;
    return (React.createElement(Modal, { isOpen: isOpen, width: "560px", onClose: handleClose, title: translate('bulk_complete_title', { namespace, count: inspectionCount }), footer: React.createElement(React.Fragment, null,
            React.createElement(Button, { onClick: handleClose, variant: "secondary", disabled: isProcessing || isLoadingData }, translate('cancel', { namespace: 'features.commons' })),
            React.createElement(Button, { onClick: handleComplete, variant: "primary", disabled: isProcessing || isLoadingData || (selectAll && (!allInspectionIds || allInspectionIds.length === 0)) }, isProcessing || isLoadingData ? (React.createElement("i", { className: "fa-light fa-spinner-third fa-spin" })) : (translate('complete_all', { namespace })))) },
        isLoadingData ? (React.createElement("div", { className: "complete-review-modal__loading" },
            React.createElement("i", { className: "fa-light fa-spinner-third fa-spin me-2" }),
            translate('loading', { namespace: 'generic' }))) : loadError ? (React.createElement("div", { className: "complete-review-modal__error" },
            React.createElement("i", { className: "fa-light fa-circle-exclamation me-2" }),
            translate('load_inspection_ids_error', { namespace }),
            React.createElement(Button, { onClick: fetchAllInspectionIds, variant: "secondary", className: "ms-3" }, translate('retry', { namespace: 'features.commons' })))) : (React.createElement(React.Fragment, null,
            React.createElement("div", { className: "complete-review-modal__alert" },
                React.createElement("i", { className: "fa-light fa-circle-exclamation complete-review-modal__alert-icon" }),
                React.createElement("span", { dangerouslySetInnerHTML: {
                        __html: translate('bulk_complete_unresolved_warning', {
                            namespace,
                            issueCount: totalUnresolvedIssues,
                            inspectionCount
                        })
                    } })),
            React.createElement("br", null),
            React.createElement("div", { className: "mb-3" },
                React.createElement("p", { className: "mb-3" }, translate('bulk_complete_description', { namespace, count: inspectionCount }))))),
        React.createElement("div", { className: "mb-3" },
            React.createElement("p", { className: "mb-3" }, translate('bulk_complete_review_description', { namespace })),
            React.createElement("div", { className: "d-flex" },
                React.createElement(Label, { htmlFor: "bulkStatusInput", className: "info-key" },
                    translate('status', { namespace: 'features.commons' }),
                    ":"),
                React.createElement(InspectionStatusSelector, { id: "bulkStatusInput", showSearch: true, maxHeight: 280, allowClear: true, value: selectedStatus || null, onChange: handleStatusSelect })))));
}
export default BulkCompleteReviewModal;
